import { useMemo, useContext } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  Alert,
  useTheme,
  Box,
  DialogActions,
  Stack,
} from "@mui/material";

import { AppContext } from "../../contextProviders/AppContext";

import { getHours, isBefore, isValid, set, getMinutes } from "date-fns";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro";
import { translation } from "../Dictionary";
import TimePickerComponent from "./TimePickerComponent";

export default function DateRangeMobileDialog({
  companyOpenHours,
  getMinimumDate,
  openDateRangeMobile,
  returnTimeLabel,
  setOpenDateRangeMobile,
  startTimeLabel,
  companyOpenHoursReturn,
  handleChangeDate,
  selectedPicker,
  setSelectedPicker,
  deliveryBufferTime,
}) {
  const {
    language,
    endDateString,
    startDateString,
    selectedCompany,
    VUOKRAAPAKU_SPECIAL_COMPANY,
    selectedDateRange,
    selectedTimeFrame,
  } = useContext(AppContext);
  const theme = useTheme();

  function handleAdvance() {
    // handleSearch(selectedDateRange[0], selectedDateRange[1]);
    setSelectedPicker(null);
    setOpenDateRangeMobile(false);
  }

  const title = useMemo(() => {
    if (!selectedCompany) return translation.get(language, "Error: No company");
    if (selectedCompany && !selectedDateRange[0])
      return translation.get(language, "Select pickup date");
    if (selectedCompany && selectedDateRange[0] && !selectedDateRange[1])
      return translation.get(language, "Select return date");

    if (selectedCompany && selectedDateRange[0] && selectedDateRange[1]) {
      return translation.get(language, "Reservation timeframe selected");
    }
  }, [selectedDateRange, selectedCompany, language]);

  const deliveryBufferHoursLabel = useMemo(() => {
    if (selectedCompany?.deliveryBufferHours) {
      if (language === "fi") {
        return (
          <Typography variant="caption" align="center">
            Varauksen voi tehdä aikaisintaan{" "}
            {selectedCompany?.deliveryBufferHours} tunnin kuluttua.
          </Typography>
        );
      }
      if (language === "se") {
        return (
          <Typography variant="caption" align="center">
            Du kan göra en reservation tidigast{" "}
            {selectedCompany?.deliveryBufferHours} timmar från nuvarande
            ögonblick.
          </Typography>
        );
      }
      if (language === "en") {
        return (
          <Typography variant="caption" align="center">
            You can make a reservation no earlier than{" "}
            {selectedCompany?.deliveryBufferHours} hours from the current
            moment.
          </Typography>
        );
      }
    }
    return null;
  }, [language, selectedCompany?.deliveryBufferHours]);

  return (
    <Dialog
      open={openDateRangeMobile}
      scroll="paper"
      PaperProps={{
        style: {
          borderRadius: "14px",
          margin: "10px",
        },
      }}
    >
      <DialogContent
        sx={{
          backgroundColor: theme.palette.primary.dialogBackground,
          padding: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Stack alignItems={"center"} flexDirection={"column"}>
            <Box
              sx={{
                transition: "250ms ease-in-out",
                display: "flex",
                flexDirection: "column",
                //   backgroundColor: titleBgColor(),
                borderRadius: "7px",
                marginTop: "5px",
                color: theme.palette.primary.dateRangeTitle,
              }}
            >
              <Typography
                sx={{
                  fontWeight: "normal",
                  fontFamily: "Sofia Pro",
                  fontSize: "18px",
                  marginY: "3px",
                }}
                align="center"
              >
                {title}
              </Typography>
            </Box>
            {deliveryBufferHoursLabel}
          </Stack>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              padding: "5px",
              flexDirection: "column",
            }}
          >
            <StaticDateRangePicker
              minDate={getMinimumDate()}
              displayStaticWrapperAs="desktop"
              calendars={1}
              disabled={!selectedCompany}
              open={true}
              disablePast
              onChange={(newValue) => handleChangeDate(newValue)}
              value={selectedDateRange}
              shouldDisableDate={(newValue) => {
                return isBefore(
                  set(newValue, {
                    hours: getHours(deliveryBufferTime),
                    minutes: getMinutes(deliveryBufferTime),
                    seconds: 59,
                  }),
                  deliveryBufferTime
                );
              }}
              sx={{
                margin: 0,
                padding: 0,
                maxHeight: "320px",
              }}
            />
            <Stack
              direction={"row"}
              sx={{
                width: "100%",
                justifyContent: "space-evenly",
                height: "54px",

                alignItems: "center",
              }}
              spacing={2}
            >
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TimePickerComponent
                  type={"startTime"}
                  dateString={startDateString}
                  selectedPicker={selectedPicker}
                  companyOpenHours={companyOpenHours}
                  companyOpenHoursReturn={companyOpenHoursReturn}
                  setSelectedPicker={setSelectedPicker}
                  title={startTimeLabel}
                  selectedTimeFrame={selectedTimeFrame}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TimePickerComponent
                  selectedPicker={selectedPicker}
                  setSelectedPicker={setSelectedPicker}
                  companyOpenHours={companyOpenHours}
                  companyOpenHoursReturn={companyOpenHoursReturn}
                  selectedDateRange={selectedDateRange}
                  type={"returnTime"}
                  title={returnTimeLabel}
                  dateString={endDateString}
                  selectedTimeFrame={selectedTimeFrame}
                />
              </Box>
            </Stack>
          </Box>

          <Grid container alignItems="center" direction="column">
            {VUOKRAAPAKU_SPECIAL_COMPANY && (
              <Grid item>
                <Alert
                  variant="filled"
                  //severity={startEndSameDay ? "info" : "success"}
                  style={{
                    transition: "350ms ease-in-out",
                  }}
                >
                  Voit halutessasi valita palautuksen seuraavalle päivälle
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack
          direction={"row"}
          width={"100%"}
          justifyContent={"space-between"}
        >
          <Button
            color="primary"
            onClick={() => handleAdvance()}
            disabled={!isValid(selectedDateRange[1])}
          >
            {translation.get(language, "Cancel text")}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => handleAdvance()}
            disabled={!isValid(selectedDateRange[1])}
            sx={{ borderBottomRightRadius: "10px" }}
          >
            OK
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
